import { useTranslation } from "react-i18next";

const FLTT_LEVELS = ["A1", "A2", "A3", "B1", "B2", "B3", "C1", "C2", "C3", "D"];
const RECREATIONAL_LEVELS = [
  "Beginner",
  "Intermediate",
  "Advanced",
  "Professional",
];

/** Empty form state when auto-join is off / unset. */
export function emptyJoinRulesForm() {
  return {
    auto_join: false,
    priority: 20,
    classementMode: "none",
    classementValue: "C3",
    unranked: false,
    recreational: [],
    note: "",
  };
}

/** Map API `gr_join_rules` → form state. */
export function joinRulesToForm(raw) {
  const rules =
    raw && typeof raw === "object"
      ? raw
      : typeof raw === "string" && raw.trim()
        ? (() => {
            try {
              return JSON.parse(raw);
            } catch {
              return null;
            }
          })()
        : null;

  if (!rules || typeof rules !== "object") return emptyJoinRulesForm();

  const cfg = rules.classement || {};
  let classementMode = "none";
  let classementValue = "C3";
  if (cfg.gte) {
    classementMode = "gte";
    classementValue = String(cfg.gte).toUpperCase();
  } else if (cfg.lt) {
    classementMode = "lt";
    classementValue = String(cfg.lt).toUpperCase();
  } else if (cfg.gt) {
    classementMode = "gte";
    classementValue = String(cfg.gt).toUpperCase();
  } else if (cfg.lte) {
    classementMode = "lt";
    classementValue = String(cfg.lte).toUpperCase();
  }

  return {
    auto_join: Boolean(rules.auto_join),
    priority: Number(rules.priority) > 0 ? Number(rules.priority) : 20,
    classementMode,
    classementValue: FLTT_LEVELS.includes(classementValue)
      ? classementValue
      : "C3",
    unranked: Boolean(rules.unranked),
    recreational: Array.isArray(rules.recreational_level_names)
      ? rules.recreational_level_names.filter((n) =>
          RECREATIONAL_LEVELS.includes(n)
        )
      : [],
    note: rules.schedule?.note || "",
  };
}

/** Map form state → API payload (or null when auto-join off). */
export function formToJoinRules(form) {
  if (!form?.auto_join) return null;
  const rules = {
    auto_join: true,
    priority: Math.max(1, Number(form.priority) || 20),
    unranked: Boolean(form.unranked),
  };
  if (form.classementMode === "gte" && form.classementValue) {
    rules.classement = { gte: form.classementValue };
  } else if (form.classementMode === "lt" && form.classementValue) {
    rules.classement = { lt: form.classementValue };
  }
  if (form.recreational?.length) {
    rules.recreational_level_names = [...form.recreational];
  }
  if (form.note?.trim()) {
    rules.schedule = { note: form.note.trim() };
  }
  return rules;
}

export function GroupJoinRulesFields({ value, onChange }) {
  const { t } = useTranslation();
  const form = value || emptyJoinRulesForm();

  const patch = (partial) => onChange({ ...form, ...partial });

  const toggleRecreational = (name) => {
    const set = new Set(form.recreational || []);
    if (set.has(name)) set.delete(name);
    else set.add(name);
    patch({ recreational: [...set] });
  };

  return (
    <div className="eg-join-rules">
      <label className="eg-check">
        <input
          type="checkbox"
          checked={Boolean(form.auto_join)}
          onChange={(e) => patch({ auto_join: e.target.checked })}
        />
        <span>
          {t(
            "groupJoinAuto",
            "Auto-assign players who register to this group"
          )}
        </span>
      </label>

      {form.auto_join ? (
        <>
          <p className="eg-hint">
            {t(
              "groupJoinAutoHint",
              "At registration, the player joins the first matching auto-join group (lowest priority number wins)."
            )}
          </p>

          <div className="eg-join-rules__row">
            <div className="eg-field">
              <label htmlFor="join-priority">
                {t("groupJoinPriority", "Priority")}
              </label>
              <input
                id="join-priority"
                className="eg-input"
                type="number"
                min="1"
                max="999"
                value={form.priority}
                onChange={(e) =>
                  patch({
                    priority: Math.max(1, parseInt(e.target.value, 10) || 1),
                  })
                }
              />
            </div>
            <div className="eg-field">
              <label htmlFor="join-classement-mode">
                {t("groupJoinClassement", "FLTT classement")}
              </label>
              <select
                id="join-classement-mode"
                className="eg-select"
                value={form.classementMode}
                onChange={(e) => patch({ classementMode: e.target.value })}
              >
                <option value="none">
                  {t("groupJoinClassementNone", "No classement filter")}
                </option>
                <option value="gte">
                  {t("groupJoinClassementGte", "Classement ≥")}
                </option>
                <option value="lt">
                  {t("groupJoinClassementLt", "Classement <")}
                </option>
              </select>
            </div>
            {form.classementMode !== "none" ? (
              <div className="eg-field">
                <label htmlFor="join-classement-value">
                  {t("groupJoinClassementLevel", "Level")}
                </label>
                <select
                  id="join-classement-value"
                  className="eg-select"
                  value={form.classementValue}
                  onChange={(e) => patch({ classementValue: e.target.value })}
                >
                  {FLTT_LEVELS.map((lvl) => (
                    <option key={lvl} value={lvl}>
                      {lvl}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>

          <label className="eg-check">
            <input
              type="checkbox"
              checked={Boolean(form.unranked)}
              onChange={(e) => patch({ unranked: e.target.checked })}
            />
            <span>
              {t(
                "groupJoinUnranked",
                "Also accept players with no FLTT classement"
              )}
            </span>
          </label>

          <fieldset className="eg-join-rules__levels">
            <legend>
              {t(
                "groupJoinRecreational",
                "Recreational levels (when no FLTT classement)"
              )}
            </legend>
            <div className="eg-join-rules__checks">
              {RECREATIONAL_LEVELS.map((name) => (
                <label key={name} className="eg-check eg-check--compact">
                  <input
                    type="checkbox"
                    checked={(form.recreational || []).includes(name)}
                    onChange={() => toggleRecreational(name)}
                  />
                  <span>
                    {t(name.toLowerCase(), name)}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="eg-field">
            <label htmlFor="join-note">
              {t("groupJoinNote", "Note (optional)")}
            </label>
            <input
              id="join-note"
              className="eg-input"
              type="text"
              maxLength={200}
              value={form.note || ""}
              placeholder={t(
                "groupJoinNoteHint",
                "e.g. Advanced group — FLTT ≥ C3"
              )}
              onChange={(e) => patch({ note: e.target.value })}
            />
          </div>
        </>
      ) : (
        <p className="eg-hint">
          {t(
            "groupJoinOffHint",
            "Players are not assigned here automatically. You can still add them manually."
          )}
        </p>
      )}
    </div>
  );
}
